import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type PropsWithChildren,
  type ReactNode
} from "react";
import { Link, useLocation } from "react-router-dom";

const SITE_ORIGIN = "https://anytoolonline.com";

const ORG_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Any Tool Online",
  "url": SITE_ORIGIN,
  "description": "Free, private browser-based tools for developers and everyday users. No accounts, no uploads, no data retention."
});

const SHARE_TEXT_RAW = "Free online tools - no signup, no hassle.";
const SHARE_TEXT = encodeURIComponent(SHARE_TEXT_RAW);

const SHARE_PLATFORMS = (url: string) => [
  { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
  { label: "X", href: `https://x.com/intent/tweet?url=${url}&text=${SHARE_TEXT}` },
  { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
  { label: "Reddit", href: `https://www.reddit.com/submit?url=${url}&title=${SHARE_TEXT}` },
  {
    label: "WhatsApp",
    href: "https://wa.me/?text=" + encodeURIComponent(SHARE_TEXT_RAW + " " + decodeURIComponent(url))
  },
  { label: "Telegram", href: `https://t.me/share/url?url=${url}&text=${SHARE_TEXT}` }
];

export function AdBlockBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [detected, setDetected] = useState(false);
  const baitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = baitRef.current;
    if (!el) return;
    // Short delay lets ad-blocker content scripts run before we measure
    const t = setTimeout(() => {
      const s = globalThis.getComputedStyle(el);
      if (el.offsetHeight === 0 || s.display === "none" || s.visibility === "hidden") {
        setDetected(true);
      }
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const encodedUrl = encodeURIComponent(globalThis.location.href);

  return (
    <>
      {/* Bait element: ad blockers hide elements with these class names, which we detect */}
      <div
        aria-hidden="true"
        className="adsbox ad-banner"
        ref={baitRef}
        style={{ height: "1px", left: "-9999px", pointerEvents: "none", position: "fixed", top: "-9999px", width: "1px" }}
      />
      {detected && !dismissed ? (
        <output className="adblock-banner">
          <p className="adblock-banner-msg">
            Using an ad blocker? We understand.{" "}
            <strong>Please share our site to support us!</strong>
          </p>
          <div className="adblock-share-links">
            {SHARE_PLATFORMS(encodedUrl).map((s) => (
              <a
                className="adblock-share-btn"
                href={s.href}
                key={s.label}
                rel="noopener noreferrer"
                target="_blank"
              >
                {s.label}
              </a>
            ))}
          </div>
          <button
            aria-label="Dismiss"
            className="adblock-banner-close"
            onClick={() => setDismissed(true)}
            type="button"
          >
            ✕
          </button>
        </output>
      ) : null}
    </>
  );
}

const COOKIE_CONSENT_KEY = "cookie-consent";

declare function gtag(...args: unknown[]): void;

function CookieBanner() {
  const [decided, setDecided] = useState(
    () => localStorage.getItem(COOKIE_CONSENT_KEY) !== null
  );

  if (decided) return null;

  function accept() {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted"
    });
    setDecided(true);
  }

  function reject() {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setDecided(true);
  }

  return (
    <section aria-label="Cookie notice" className="cookie-banner">
      <p className="cookie-banner-text">
        We use Google Analytics and Google AdSense, which may set cookies.{" "}
        <Link to="/privacy">Privacy Policy</Link>
      </p>
      <div className="cookie-banner-actions">
        <button className="cookie-banner-reject" onClick={reject} type="button">
          Reject
        </button>
        <button className="cookie-banner-ok" onClick={accept} type="button">
          Accept
        </button>
      </div>
    </section>
  );
}

const NAV_ITEMS = [
  { href: "/qr", label: "QR Code" },
  { href: "/pdf/merge", label: "PDF Merge" },
  { href: "/pdf/convert", label: "PDF Convert" },
  { href: "/format", label: "Formatter" },
  { href: "/image/compress", label: "Image" },
  { href: "/base64", label: "Base64" },
  { href: "/hash", label: "Hash" },
  { href: "/word-count", label: "Word Count" }
];

export type AppFrameProps = PropsWithChildren<{
  footer?: ReactNode;
  title: string;
}>;

export function AppFrame({ children, footer, title }: AppFrameProps) {
  const { pathname } = useLocation();
  const currentPath = pathname.replace(/\/$/, "");
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [pathname]);

  return (
    <div className="app-frame">
      <script dangerouslySetInnerHTML={{ __html: ORG_SCHEMA }} type="application/ld+json" />
      <link href={`${SITE_ORIGIN}${currentPath || "/"}`} rel="canonical" />
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <header className="app-header">
        <Link className="brand-mark" to="/">
          Any Tool Online
        </Link>
        <nav aria-label="Tools" className="app-nav">
          {NAV_ITEMS.map((item) => {
            const active = currentPath.startsWith(item.href);
            return (
              <Link aria-current={active ? "page" : undefined} key={item.href} to={item.href}>
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>
      <main id="main-content">
        <section className="hero-panel">
          <h1 ref={h1Ref} tabIndex={-1}>{title}</h1>
        </section>
        {children}
      </main>
      <footer className="app-footer">
        {footer ?? (
          <nav aria-label="Footer">
            <p>
              Stateless utilities. No accounts. No saved content. Built for quick, focused jobs.
              {" · "}
              <Link to="/about">About</Link>
              {" · "}
              <Link to="/contact">Contact</Link>
              {" · "}
              <Link to="/privacy">Privacy</Link>
              {" · "}
              <Link to="/terms">Terms</Link>
            </p>
          </nav>
        )}
      </footer>
      <CookieBanner />
    </div>
  );
}

export type DropZoneProps = {
  accept: string;
  hint?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
};

export function DropZone({ accept, hint, multiple = false, onFiles }: Readonly<DropZoneProps>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleDragOver(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(event.dataTransfer.files);
    if (dropped.length > 0) onFiles(dropped);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selected.length > 0) onFiles(selected);
  }

  return (
    <button
      className={`drop-zone${isDragging ? " drop-zone-active" : ""}`}
      onClick={() => inputRef.current?.click()}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      type="button"
    >
      <input
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        ref={inputRef}
        style={{ display: "none" }}
        type="file"
      />
      <span aria-hidden="true" className="drop-zone-icon">⬆</span>
      <span>
        Drop files here or <strong>browse</strong>
      </span>
      {hint ? <small>{hint}</small> : null}
    </button>
  );
}

export type FieldProps = PropsWithChildren<{
  hint?: string;
  label: string;
}>;

export function Field({ children, hint, label }: Readonly<FieldProps>) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export type StatCardProps = {
  label: string;
  value: string;
};

export function StatCard({ label, value }: Readonly<StatCardProps>) {
  return (
    <div className="stat-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function ComingSoon({
  description,
  title
}: Readonly<{ description: string; title: string }>) {
  return (
    <article className="coming-soon-card">
      <p className="eyebrow">Coming soon</p>
      <h2>{title}</h2>
      <p>{description}</p>
      <Link to="/qr">Try the QR Code Generator first</Link>
    </article>
  );
}
